import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";

export default function SecurityTab() {
  const [me, setMe] = useState(null);
  const [password, setPassword] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("idle");

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const setup = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/setup-2fa", { password });
      setQr(r.data.qr_png);
      setSecret(r.data.secret);
      setStep("verify");
      toast.info("Scan QR dengan Google Authenticator");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/enable-2fa", { otp });
      toast.success("2FA aktif — mulai sekarang wajib OTP saat login");
      setStep("idle");
      setPassword("");
      setOtp("");
      setQr("");
      const r = await api.get("/auth/me");
      setMe(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/disable-2fa", { otp });
      toast.success("2FA dinonaktifkan");
      setOtp("");
      const r = await api.get("/auth/me");
      setMe(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const enabled = !!me?.totp_enabled;

  return (
    <div className="max-w-2xl space-y-6" data-testid="security-tab">
      <div className="border border-stone-200 bg-white p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          {enabled ? (
            <ShieldCheck className="text-green-600" size={22} />
          ) : (
            <ShieldOff className="text-stone-400" size={22} />
          )}
          <h3 className="font-display text-2xl">Two-Factor Authentication</h3>
        </div>
        <p className="text-sm text-stone-600 mb-6">
          Google Authenticator (TOTP). Status:{" "}
          <span className={`font-medium ${enabled ? "text-green-700" : "text-stone-500"}`}>
            {enabled ? "AKTIF" : "TIDAK AKTIF"}
          </span>
        </p>

        {!enabled && step === "idle" && (
          <form onSubmit={setup} className="space-y-4" data-testid="2fa-setup-form">
            <div>
              <label className="block overline text-stone-500 mb-2">Password saat ini</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="2fa-password"
                className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="2fa-setup-btn"
              className="px-5 py-3 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A]"
            >
              Aktifkan 2FA
            </button>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={enable} className="space-y-4" data-testid="2fa-verify-form">
            <div className="bg-stone-50 border border-stone-200 p-5 flex flex-col md:flex-row gap-5 items-center">
              {qr && <img src={qr} alt="QR" className="w-40 h-40" data-testid="2fa-qr" />}
              <div className="flex-1 text-sm">
                <p className="text-stone-700 mb-2">Scan QR di atas dengan aplikasi Google Authenticator.</p>
                <p className="text-xs text-stone-500 break-all">
                  Atau input manual: <code className="bg-white px-1">{secret}</code>
                </p>
              </div>
            </div>
            <div>
              <label className="block overline text-stone-500 mb-2">Masukkan 6-digit OTP</label>
              <input
                type="text"
                required
                inputMode="numeric"
                maxLength={6}
                value={otp}
                data-testid="2fa-otp"
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none font-mono text-lg tracking-widest"
              />
            </div>
            <button
              type="submit"
              data-testid="2fa-enable-btn"
              className="px-5 py-3 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A]"
            >
              Verifikasi &amp; Aktifkan
            </button>
          </form>
        )}

        {enabled && (
          <form onSubmit={disable} className="space-y-4" data-testid="2fa-disable-form">
            <p className="text-sm text-stone-600">Untuk mematikan 2FA, masukkan OTP saat ini:</p>
            <input
              type="text"
              required
              inputMode="numeric"
              maxLength={6}
              value={otp}
              data-testid="2fa-otp-disable"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none font-mono text-lg tracking-widest"
            />
            <button
              type="submit"
              data-testid="2fa-disable-btn"
              className="px-5 py-3 border border-red-500 text-red-600 hover:bg-red-50"
            >
              Nonaktifkan 2FA
            </button>
          </form>
        )}
      </div>

      <div className="border border-stone-200 bg-white p-6 md:p-8">
        <h3 className="font-display text-2xl mb-3">Kebijakan Keamanan</h3>
        <ul className="space-y-2 text-sm text-stone-600 list-disc pl-5">
          <li>Login lock 15 menit otomatis setelah 5x salah password.</li>
          <li>Session admin timeout otomatis 15 menit tidak aktif.</li>
          <li>Verifikasi Cloudflare Turnstile aktif pada form publik.</li>
          <li>URL akses admin di-obfuscate (/akses-internal-aurora).</li>
          <li>SSL/TLS bawaan Emergent aktif untuk semua rute.</li>
        </ul>
      </div>
    </div>
  );
}
